import React, { useRef, useState, useCallback } from 'react';
import { gsap } from '../../lib/gsapSetup';
import { cn } from '../../lib/cn';

/**
 * Premium FAQ accordion with GSAP-powered expand/collapse.
 * Accessible: uses proper ARIA attributes and keyboard navigation.
 */
export default function Accordion({ items, className }) {
  const [openIndex, setOpenIndex] = useState(null);
  const contentRefs = useRef([]);

  const toggle = useCallback((index) => {
    // Close previously open item
    if (openIndex !== null && openIndex !== index && contentRefs.current[openIndex]) {
      gsap.to(contentRefs.current[openIndex], {
        height: 0,
        opacity: 0,
        duration: 0.4,
        ease: 'power2.inOut',
      });
    }

    if (openIndex === index) {
      // Close current
      gsap.to(contentRefs.current[index], {
        height: 0,
        opacity: 0,
        duration: 0.4,
        ease: 'power2.inOut',
      });
      setOpenIndex(null);
    } else {
      // Open new
      const el = contentRefs.current[index];
      if (el) {
        gsap.set(el, { height: 'auto', opacity: 1 });
        const autoHeight = el.offsetHeight;
        gsap.fromTo(el,
          { height: 0, opacity: 0 },
          { height: autoHeight, opacity: 1, duration: 0.5, ease: 'power3.out' }
        );
      }
      setOpenIndex(index);
    }
  }, [openIndex]);

  return (
    <div className={cn('space-y-3', className)}>
      {items.map((item, index) => {
        const isOpen = openIndex === index;
        return (
          <div
            key={index}
            className={cn(
              'border rounded-2xl transition-colors duration-300',
              isOpen
                ? 'border-secondary/30 bg-secondary/[0.04]'
                : 'border-linx_border hover:border-linx_border-hover bg-transparent'
            )}
          >
            <button
              onClick={() => toggle(index)}
              className="w-full flex items-center justify-between px-6 py-5 text-left group"
              aria-expanded={isOpen}
              aria-controls={`accordion-content-${index}`}
              id={`accordion-header-${index}`}
            >
              <span className={cn(
                'font-display font-medium text-base transition-colors duration-200',
                isOpen ? 'text-linx_text-primary' : 'text-linx_text-secondary group-hover:text-linx_text-primary'
              )}>
                {item.question}
              </span>
              <span className={cn(
                'flex-shrink-0 ml-4 w-8 h-8 rounded-lg flex items-center justify-center transition-all duration-300',
                isOpen ? 'bg-secondary/15 rotate-45' : 'bg-primary/[0.04] rotate-0'
              )}>
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M7 1V13M1 7H13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"
                    className={cn('transition-colors', isOpen ? 'text-secondary-dark' : 'text-linx_text-muted')}
                  />
                </svg>
              </span>
            </button>
            <div
              ref={el => contentRefs.current[index] = el}
              role="region"
              aria-labelledby={`accordion-header-${index}`}
              id={`accordion-content-${index}`}
              className="overflow-hidden"
              style={{ height: 0, opacity: 0 }}
            >
              <div className="px-6 pb-5 text-linx_text-secondary text-[15px] leading-relaxed">
                {item.answer}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
